import { Controller } from "@hotwired/stimulus"
import { FPS, secondsToFrame } from "../utils/aoe2_time"

// Root coordinator for the AOE2 clip review editor.
//
// Owns the shared <video> element + scrubber and acts as the event bus between
// the trim (`aoe2-trim`) and reframe (`aoe2-reframe-editor`) child controllers:
//
//   - emits  `clip:timeupdate` { seconds, frame }  whenever playback moves
//   - listens for `clip:seek` { seconds }           to move the video
//   - emits  `clip:serialize`                       on form submit so children
//                                                    flush their hidden fields
//
// All math lives in utils/aoe2_time — this controller only wires DOM to events.
// Events are dispatched on THIS element (the review root that wraps the whole
// editor); children resolve the same element via `closest(...)`, so bus events
// stay scoped to a single editor instance (no window pollution).
export default class extends Controller {
  static targets = ["video", "scrubber", "timeLabel"]
  static values = {
    duration: { type: Number, default: 0 },
    fps: { type: Number, default: FPS },
  }

  connect() {
    this.onSeek = this.handleSeek.bind(this)
    this.element.addEventListener("clip:seek", this.onSeek)
  }

  disconnect() {
    this.element.removeEventListener("clip:seek", this.onSeek)
  }

  // Bound to the <video> `timeupdate` + `loadedmetadata` + `seeked` events.
  timeupdate() {
    const seconds = this.currentSeconds()
    this.reflectScrubber(seconds)
    this.emitTimeupdate(seconds)
  }

  // Bound to the scrubber range `input` event.
  scrub() {
    if (!this.hasScrubberTarget) return
    this.seekTo(Number(this.scrubberTarget.value))
  }

  // Bound (via `clip:seek`) to child requests to move the playhead.
  handleSeek(event) {
    const seconds = Number(event.detail && event.detail.seconds)
    if (Number.isFinite(seconds)) this.seekTo(seconds)
  }

  seekTo(seconds) {
    const clamped = this.clampToDuration(seconds)
    if (this.hasVideoTarget) this.videoTarget.currentTime = clamped
    this.reflectScrubber(clamped)
    // Emit immediately so the crop box / playhead track even while paused
    // (a bare currentTime set does not always fire `timeupdate` in every browser).
    this.emitTimeupdate(clamped)
  }

  reflectScrubber(seconds) {
    if (this.hasScrubberTarget && document.activeElement !== this.scrubberTarget) {
      this.scrubberTarget.value = String(seconds)
    }
    if (this.hasTimeLabelTarget) this.timeLabelTarget.textContent = seconds.toFixed(2)
  }

  emitTimeupdate(seconds) {
    this.dispatch("timeupdate", {
      prefix: "clip",
      bubbles: true,
      detail: { seconds, frame: secondsToFrame(seconds) },
    })
  }

  // Bound to the wrapping form `submit` event — a belt-and-suspenders flush.
  serialize() {
    this.dispatch("serialize", { prefix: "clip", bubbles: true })
  }

  currentSeconds() {
    return this.hasVideoTarget ? Number(this.videoTarget.currentTime) || 0 : 0
  }

  clampToDuration(seconds) {
    const value = Number.isFinite(seconds) ? seconds : 0
    const max = this.durationValue > 0 ? this.durationValue : value
    return Math.max(0, Math.min(max, value))
  }
}
